import React, { FC, useMemo, useState } from 'react';
import { Collapse } from 'reactstrap';

import { maxBy, minBy, pickBy, sum, values } from 'lodash-es';

import classNames from '../../helpers/classNames';
import useTranslate from '../../hooks/useTranslate';
import { useAccommodationItemPrebook } from '../../services/apis/accommodation/api';
import {
  AccommodationItemAddonType,
  AccommodationItemPackageType,
  AccommodationItemType,
  AccommodationType,
} from '../../services/apis/accommodation/types';
import { getAccommodationItemAddonMaxQuantity } from '../../services/apis/accommodation/utils';
import useStore from '../../store/useStore';
import Button from '../Button/Button';
import styles from './AccommodationItem.module.scss';
import AccommodationItemAddons from './AccommodationItemAddons';
import AccommodationItemInfo from './AccommodationItemInfo';
import { AccommodationItemTotal } from './AccommodationItemTotal';

interface AccommodationItemProps {
  item: AccommodationItemType;
  disabled?: boolean;
  accommodationId: AccommodationType['id'];
  checkinDate: string;
  checkoutDate: string;
}

const AccommodationItem: FC<AccommodationItemProps> = props => {
  const { disabled, item, accommodationId, checkinDate, checkoutDate } = props;
  const isConfirmRequired: boolean =
    (item.hasAddons && !!item.addons && item.addons.length > 0) ||
    (!!item.numberOfBeds && item.numberOfBeds > 1) ||
    (item.hasPackages && !!item.packages && item.packages.length > 0);
  const { translate } = useTranslate();
  const [opened, setOpened] = useState(false);
  const maxNumberOfGuestsByPricing = Math.max(
    ...item.pricing.pricingDates.map(({ numberOfGuests }) => numberOfGuests)
  );
  const maxNumberOfGuests = Math.min(
    maxNumberOfGuestsByPricing,
    item.numberOfBeds || maxNumberOfGuestsByPricing
  );
  const [numberOfGuests, setNumberOfGuests] = useState<number>(maxNumberOfGuests);
  const [selectedAddons, setSelectedAddons] = useState<Record<number, number>>({});
  const [selectedPackages, setSelectedPackages] = useState<Record<number, number>>({});
  const personsInSelectedPackages = useMemo(
    () =>
      sum(
        values(
          pickBy(
            selectedPackages,
            (_, key) =>
              item.packages?.find(({ id }) => String(id) === key)?.chargeType === 'PerPerson'
          )
        )
      ),
    [selectedPackages, item]
  );
  const itemCurrentPrice =
    (personsInSelectedPackages && personsInSelectedPackages >= numberOfGuests
      ? 0
      : item.pricing.pricingDates.find(
          pricingDate => pricingDate.numberOfGuests === numberOfGuests - personsInSelectedPackages
        )?.sellPrice) ?? item.pricing.sellPrice;
  const selectedAddonsTotal = Object.entries(selectedAddons)
    .map(([id, quantity]) => {
      const price = item.addons?.find(addon => String(addon.id) === id)?.price ?? 0;

      return quantity * price;
    })
    .reduce((a, b) => a + b, 0);

  const selectedPackagesTotal = Object.entries(selectedPackages)
    .map(([id, quantity]) => {
      const prices = item.packages?.find(pkg => String(pkg.id) === id)?.prices;
      const price =
        (prices?.find(({ minCount }) => minCount === quantity)?.price ||
          minBy(prices, 'minCount')?.price) ??
        0;

      return quantity * price;
    })
    .reduce((a, b) => a + b, 0);

  const selectedPackagesCount = Object.entries(selectedPackages)
    .map(([, quantity]) => quantity)
    .reduce((a, b) => a + b, 0);

  const total = itemCurrentPrice + selectedAddonsTotal + selectedPackagesTotal;
  const isLoggedIn = useStore(state => state.isLoggedIn);
  const openAuthModal = useStore(state => state.openAuthModal);
  const prebook = useAccommodationItemPrebook();

  const handleOpen = () => {
    setOpened(true);
  };

  const handleClose = () => {
    setOpened(false);
    prebook.reset();
  };

  const handlePrebook = () => {
    if (!isLoggedIn) {
      openAuthModal(true);
      return;
    }

    const addons = item.addons ?? [];
    const packages = item.packages ?? [];

    prebook
      .mutateAsync({
        accommodationId,
        checkinDate,
        checkoutDate,
        origin: 'website',
        accommodations: [
          {
            id: item.id,
            numberOfGuests,
            addons: Object.entries(selectedAddons)
              .map(([id, itemCount]) => ({
                id: Number(id),
                name: (addons.find(addon => addon.id === Number(id)) as AccommodationItemAddonType)
                  .name,
                itemCount,
              }))
              .filter(({ itemCount }) => itemCount > 0),
            packages: Object.entries(selectedPackages)
              .map(([id, itemCount]) => ({
                id: Number(id),
                name: (
                  packages.find(pack => pack.id === Number(id)) as AccommodationItemPackageType
                ).name,
                itemCount,
              }))
              .filter(({ itemCount }) => itemCount > 0),
          },
        ],
      })
      .then(() => {
        handleReset();
      });
  };

  const handleReset = () => {
    handleClose();
    setSelectedAddons({});
    setSelectedPackages({});
    setNumberOfGuests(maxNumberOfGuests);
  };

  console.log('item :>> ', item);

  return (
    <div
      className={classNames(
        styles.items,
        item.available && opened && styles.opened,
        (disabled || prebook.isLoading) && styles.disabled
      )}
    >
      <AccommodationItemInfo
        className={styles.item}
        item={item}
        disabled={disabled}
        loading={prebook.isLoading}
        onAdd={isConfirmRequired ? (opened ? undefined : handleOpen) : handlePrebook}
      />
      {!!prebook.error && !isConfirmRequired && (
        <div className="text-danger mx-3 mb-3">{String(prebook.error)}</div>
      )}
      {item.available && isConfirmRequired && (
        <Collapse isOpen={opened}>
          {!!item.numberOfBeds && item.numberOfBeds > 0 && maxNumberOfGuests > 0 && (
            <AccommodationItemAddons
              className={styles.item}
              items={[
                {
                  id: 0,
                  currency: item.currency,
                  description: '',
                  label: String(translate('accommodation.numberOfGuests')),
                  price: itemCurrentPrice,
                  min: 1,
                  max: maxNumberOfGuests,
                  quantity: numberOfGuests,
                },
              ]}
              disabled={disabled || prebook.isLoading}
              onChange={({ id }, value) => setNumberOfGuests(value.quantity)}
            />
          )}

          {!!item.packages && item.packages.length > 0 && (
            <AccommodationItemAddons
              title={String(translate('accommodation.packages'))}
              className={styles.item}
              items={item.packages.map(
                ({ name, id, price, description, chargeType, prices, addons }) => ({
                  currency: item.currency,
                  description: (
                    <>
                      <div>{description}</div>
                      {addons?.map(addon => (
                        <div key={addon.name} className="ps-4">
                          {addon.numberOfAddonsInclded}&times; {addon.name}
                          {!!addon.description && <> ({addon.description})</>}
                        </div>
                      ))}
                    </>
                  ),
                  id,
                  label: name,
                  price:
                    prices.find(({ minCount }) => selectedPackages[id] === minCount)?.price ||
                    minBy(prices, 'minCount')?.price,
                  quantity: selectedPackages[id] ?? 0,
                  subLabel: String(translate(`accommodation.chargeType.${chargeType}`)),
                  min: 0,
                  max:
                    chargeType === 'PerPerson' &&
                    personsInSelectedPackages >= (numberOfGuests || item.numberOfBeds || 0)
                      ? selectedPackages[id]
                      : maxBy(prices, 'minCount')?.minCount ||
                        getAccommodationItemAddonMaxQuantity(chargeType),
                })
              )}
              disabled={disabled || prebook.isLoading}
              onChange={({ id }, value) =>
                setSelectedPackages(packages => ({
                  ...packages,
                  [id]: value.quantity,
                }))
              }
            />
          )}

          {!!item.addons && item.addons.length > 0 && (
            <AccommodationItemAddons
              title={String(translate('accommodation.extraServices'))}
              className={styles.item}
              items={item.addons.map(({ name, id, price, description, chargeType }) => ({
                currency: item.currency,
                description: description || '',
                id,
                label: String(translate(`accommodation.addons.${name}`)),
                price,
                quantity: selectedAddons[id] ?? 0,
                subLabel: String(translate(`accommodation.chargeType.${chargeType}`)),
                min: 0,
                max: getAccommodationItemAddonMaxQuantity(chargeType),
              }))}
              disabled={disabled || prebook.isLoading}
              onChange={({ id }, value) =>
                setSelectedAddons(addons => ({
                  ...addons,
                  [id]: value.quantity,
                }))
              }
            />
          )}          
          <AccommodationItemTotal
            className={styles.item}
            value={total}
            currencyName={item.currency.name}
          />
          <div className="d-flex pb-3 px-3 gap-3 justify-content-end align-items-center">
            {!!prebook.error && <div className="text-danger">{String(prebook.error)}</div>}
            {item.clubOffersPackagesOnly && selectedPackagesCount === 0 && (<div className="text-info">{translate('accommodation.package-required')}</div>)}
            <Button
              primary
              loading={prebook.isLoading}
              disabled={disabled || prebook.isLoading || (item.clubOffersPackagesOnly && selectedPackagesCount === 0)}
              onClick={handlePrebook}
            >
              {String(translate('accommodation.confirm'))}
            </Button>
            <Button tertiary onClick={handleReset}>
              {String(translate('accommodation.discard'))}
            </Button>
          </div>
        </Collapse>
      )}
    </div>
  );
};

export default AccommodationItem;
